"use client";

import type { FC } from "react";

import type { BlogItemProps } from "@jti/ui/components/BlogListing/BlogItem/BlogItem";
import { BlogItem } from "@jti/ui/components/BlogListing/BlogItem/BlogItem";
import { Carousel } from "@jti/ui/components/Carousel";
import { ContainerHeader } from "@jti/ui/components/ContainerHeader";
import { ResponsiveContainer } from "@jti/ui/components/ResponsiveContainer";
import { getImageSource } from "@jti/ui/utils/getImageSource";

import { useLocale } from "@/context/LocaleContext";
import { buildBlogSlug } from "@/utils/buildBlogSlug";

import styles from "./BlogRelatedArticles.module.scss";

type RelatedArticle = {
  article?: {
    value?: {
      data?: BlogItemProps & {
        slug?: string;
      };
    };
  };
};

type BlogRelatedArticlesProps = {
  relatedArticlesTitle?: string;
  relatedArticlesCtaLabel?: string;
  relatedArticlesCtaUrl?: string;
  relatedArticles: RelatedArticle[];
  isLoyalty?: boolean;
};

export const BlogRelatedArticles: FC<BlogRelatedArticlesProps> = ({
  relatedArticlesTitle,
  relatedArticlesCtaLabel,
  relatedArticlesCtaUrl,
  relatedArticles,
  isLoyalty,
}) => {
  const { currentLocale } = useLocale();

  return (
    <ResponsiveContainer size="large">
      <div className={styles.content}>
        <ContainerHeader
          title={relatedArticlesTitle}
          ctaHref={relatedArticlesCtaUrl}
          ctaLabel={relatedArticlesCtaLabel}
        />
        <Carousel>
          {relatedArticles?.map(({ article = { value: { data: {} } } }, index) => {
            if (!article?.value?.data) return null;
            const {
              value: { data: rawData },
            } = article;

            const data = (rawData || {}) as BlogItemProps & {
              slug?: string;
            };
            const blogItemImage = getImageSource(data?.image || null, data?.title);
            return (
              <BlogItem
                key={data.slug || `article-${index}`}
                {...data}
                title={data.title || ""}
                description={data.description || ""}
                subtitle={data.subtitle || ""}
                readTime={data.readTime || ""}
                creationDate={data.creationDate || ""}
                image={blogItemImage}
                isFeatured={false}
                path={buildBlogSlug(data.slug, isLoyalty, currentLocale)}
              />
            );
          })}
        </Carousel>
      </div>
    </ResponsiveContainer>
  );
};
